import { useQuery, useMutation, useTransport, createConnectQueryKey } from '@connectrpc/connect-query'
import { create } from '@bufbuild/protobuf'
import { useMemo } from 'react'
import { useQueryClient } from '@tanstack/react-query'

import type { QueryClient } from '@tanstack/react-query'
import type { Transport } from '@connectrpc/connect'

import {
  getEvent,
  listUserSchedulings,
  createUserScheduling,
  updateUserScheduling,
  deleteUserScheduling,
  listEventStaffAvailability,
  listJobEvents,
} from '@/gen/cortado/v1/cortado-CortadoService_connectquery'
import {
  GetEventRequestSchema,
  ListUserSchedulingsRequestSchema,
} from '@/gen/cortado/v1/cortado_pb'
import { ListEventStaffAvailabilityRequestSchema } from '@/gen/cortado/v1/event_staffing_pb'

/**
 * Hook to get user scheduling IDs for a specific event
 *
 * This hook:
 * - Fetches the event data which includes user_scheduling_ids
 * - Returns the array of user scheduling IDs
 * - Can be used to fetch user schedulings for display
 *
 * Note: Currently, we fetch the full event to get user_scheduling_ids.
 * In the future, we may add dedicated ListUserSchedulings RPC for better performance.
 *
 * @param eventId - The ID of the event to get user schedulings for
 * @returns Query result with user scheduling IDs
 */
export function useUserSchedulingIdsForEvent(eventId: string) {
  const request = useMemo(() => {
    return create(GetEventRequestSchema, {
      id: eventId,
    })
  }, [eventId])

  const query = useQuery(
    getEvent,
    request,
    {
      enabled: !!eventId,
    }
  )

  return {
    userSchedulingIds: query.data?.event?.userSchedulingIds || [],
    scheduledUsersCount: query.data?.event?.scheduledUsersCount || 0,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
    hasLoaded: query.isSuccess || !!query.data,
  }
}

/**
 * Hook to get a full event with all user scheduling IDs and other enriched data
 *
 * This hook:
 * - Fetches the complete event data including:
 *   - Basic event fields (date, length, guests, timezone)
 *   - Contact information (full contact object)
 *   - Created/updated by users (full user objects)
 *   - User scheduling IDs (to be fetched separately)
 *   - Note IDs (to be fetched separately)
 *   - Task IDs (to be fetched separately)
 * - Returns the full enriched event
 *
 * @param eventId - The ID of the event to fetch
 * @returns Query result with full event data
 */
export function useEvent(eventId: string) {
  const request = useMemo(() => {
    return create(GetEventRequestSchema, {
      id: eventId,
    })
  }, [eventId])

  const query = useQuery(
    getEvent,
    request,
    {
      enabled: !!eventId,
      select: (data) => data?.event,
    }
  )

  return {
    event: query.data,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
    hasLoaded: query.isSuccess || !!query.data,
  }
}

/**
 * Hook to list all user schedulings for an event
 *
 * @param params - Parameters including organizationId and eventId
 * @param enabled - False holds the query back (a sheet that is not open yet)
 * @returns Query result with user schedulings array
 */
export function useUserSchedulings(params: { organizationId: string; eventId: string }, enabled = true) {
  const { organizationId, eventId } = params

  const request = useMemo(() => {
    return create(ListUserSchedulingsRequestSchema, {
      organizationId,
      eventId,
    })
  }, [organizationId, eventId])

  const query = useQuery(
    listUserSchedulings,
    request,
    {
      enabled: enabled && !!organizationId && !!eventId,
    }
  )

  return {
    schedulings: query.data?.schedulings || [],
    totalCount: query.data?.totalCount || 0,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
    hasLoaded: query.isSuccess || !!query.data,
  }
}

/**
 * Staff availability for the Assign Staff sheet: every org member, whether
 * they can work the event (overlapping bookings on the on-site window, org
 * blackout dates), and the on-site window shifts are prefilled from.
 */
export function useEventStaffAvailability(eventId: string, enabled = true) {
  const request = useMemo(
    () => create(ListEventStaffAvailabilityRequestSchema, { eventId }),
    [eventId],
  )
  const query = useQuery(listEventStaffAvailability, request, { enabled: enabled && !!eventId })
  return {
    availability: query.data,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
  }
}

/**
 * Everything that shows staffing: the event's scheduling list, the event
 * itself, the Assign Staff sheet's availability, and the job page's events
 * (staff counts and names). A role change moves the Lead between rows, so the
 * whole set is refreshed after any scheduling write.
 */
export function invalidateStaffingQueries(queryClient: QueryClient, transport: Transport) {
  const keys = [
    createConnectQueryKey({ schema: listUserSchedulings, transport, cardinality: undefined }),
    createConnectQueryKey({ schema: getEvent, transport, cardinality: undefined }),
    createConnectQueryKey({ schema: listEventStaffAvailability, transport, cardinality: undefined }),
    createConnectQueryKey({ schema: listJobEvents, transport, cardinality: undefined }),
  ]
  for (const queryKey of keys) {
    queryClient.invalidateQueries({ queryKey })
  }
}

/**
 * Hook to create a new user scheduling
 *
 * @returns Mutation hook for creating user scheduling
 */
export function useCreateUserScheduling() {
  const queryClient = useQueryClient()
  const transport = useTransport()

  return useMutation(createUserScheduling, {
    onSuccess: () => invalidateStaffingQueries(queryClient, transport),
  })
}

/**
 * Hook to update an existing user scheduling (shift, status, or role)
 *
 * @returns Mutation hook for updating user scheduling
 */
export function useUpdateUserScheduling() {
  const queryClient = useQueryClient()
  const transport = useTransport()

  return useMutation(updateUserScheduling, {
    onSuccess: () => invalidateStaffingQueries(queryClient, transport),
  })
}

/**
 * Hook to delete a user scheduling
 *
 * @returns Mutation hook for deleting user scheduling
 */
export function useDeleteUserScheduling() {
  const queryClient = useQueryClient()
  const transport = useTransport()

  return useMutation(deleteUserScheduling, {
    onSuccess: () => invalidateStaffingQueries(queryClient, transport),
  })
}
